import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useFetch } from "@/hooks/useApi";
import { PageHeader, Loader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { gbp } from "@/lib/format";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { data, loading } = useFetch("/settings");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setForm(data); }, [data]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", {
        base_rate_per_stop: parseFloat(form.base_rate_per_stop),
        fuel_variance_pct: parseFloat(form.fuel_variance_pct),
        daily_fuel_budget: parseFloat(form.daily_fuel_budget),
        weekly_fuel_budget: parseFloat(form.weekly_fuel_budget),
        monthly_fuel_budget: parseFloat(form.monthly_fuel_budget),
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading || !form) return <Loader />;

  return (
    <div className="fp-fade-in max-w-2xl">
      <PageHeader title="Company Settings" subtitle="Base rates, fuel variance tolerance & budgets" testId="settings-page" />
      <Card className="p-6 space-y-5">
        <div>
          <Label>Base Rate per Stop (£)</Label>
          <Input type="number" step="0.01" value={form.base_rate_per_stop} onChange={set("base_rate_per_stop")} className="mt-1.5 font-mono" data-testid="base-rate-input" />
          <p className="text-xs text-muted-foreground mt-1">Earnings = Approved Stops × Base Rate × Driver Multiplier. Current: {gbp(form.base_rate_per_stop)}</p>
        </div>
        <div>
          <Label>Acceptable Fuel Variance (%)</Label>
          <Input type="number" value={form.fuel_variance_pct} onChange={set("fuel_variance_pct")} className="mt-1.5 font-mono" data-testid="variance-input" />
          <p className="text-xs text-muted-foreground mt-1">Fuel usage within this % is "Within Target".</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>Daily Fuel Budget (£)</Label><Input type="number" value={form.daily_fuel_budget} onChange={set("daily_fuel_budget")} className="mt-1.5 font-mono" data-testid="daily-budget-input" /></div>
          <div><Label>Weekly Budget (£)</Label><Input type="number" value={form.weekly_fuel_budget} onChange={set("weekly_fuel_budget")} className="mt-1.5 font-mono" data-testid="weekly-budget-input" /></div>
          <div><Label>Monthly Budget (£)</Label><Input type="number" value={form.monthly_fuel_budget} onChange={set("monthly_fuel_budget")} className="mt-1.5 font-mono" data-testid="monthly-budget-input" /></div>
        </div>
        <Button onClick={save} disabled={busy} data-testid="save-settings-button">
          {busy ? <Icons.LoaderCircle className="h-4 w-4 animate-spin" /> : "Save Settings"}
        </Button>
      </Card>
    </div>
  );
}
